import qs from 'qs';

import { DeepPick, FirebaseResponseItem, createWhereQuery, whereClause } from '../utils/fetching-helpers';
import { unstableCachedFetch } from './unstable-cached-fetch';

class ContentFetcher<TData> {
  private contentForm: string;
  private limit?: number;
  private status?: Array<'published' | 'draft'>;
  private where?: { [key: string]: string };
  private omitFields?: string[];
  private visibilityId?: string;
  private depth?: number;
  private tags?: string[];

  constructor(contentForm: string) {
    this.contentForm = contentForm;
  }

  public setLimit(limit: number): ContentFetcher<TData> {
    this.limit = limit;
    return this;
  }

  public setDepth(depth: number): ContentFetcher<TData> {
    this.depth = depth;
    return this;
  }

  public setStatus(status: Array<'published' | 'draft'>): ContentFetcher<TData> {
    this.status = status;
    return this;
  }

  public setWhere(where: whereClause[]): ContentFetcher<TData> {
    this.where = createWhereQuery(where);
    return this;
  }

  public omit(fields: string[]): ContentFetcher<TData> {
    this.omitFields = fields;
    return this;
  }

  public setTags(fields: string[]): ContentFetcher<TData> {
    this.tags = fields;
    return this;
  }

  public setVisibilityId(fields: string): ContentFetcher<TData> {
    this.visibilityId = fields;
    return this;
  }

  public async fetch<const TSelected extends readonly string[] | undefined>(
    selectFields: TSelected = undefined as TSelected,
  ) {
    const query = {
      select: selectFields,
      limit: this.limit,
      status: this.status,
      depth: this.depth || 0,
      omit: this.omitFields,
      visibilityId: this.visibilityId,
      assetMeta: true,
      ...this.where,
    };

    const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    const url = `${baseUrl}/api/v2/${this.contentForm}?${qs.stringify(query, {
      encodeValuesOnly: true,
      arrayFormat: 'comma',
    })}`;

    const headers = new Headers({
      'x-24-org': process.env.NEXT_PUBLIC_API_KEY || '',
    });

    const data = await unstableCachedFetch<{
      data: TSelected extends readonly string[]
        ? DeepPick<FirebaseResponseItem<TData>, TSelected[number]>[]
        : FirebaseResponseItem<TData>[];
    }>({ headers, tags: this.tags, url });

    return data?.data;
  }
}

export const getContent = <TData>(contentForm: string) => {
  return new ContentFetcher<TData>(contentForm);
};
